import { AppEventSdk, DomainInitPayload } from '@sxm-de/dev-kits/web-sdk/types';
import { ProductRedeemUrl } from '@sxm-de/dev-kits/web-sdk/types';
import { getPartnerEntitlement } from '../helpers/get-partner-entitlement';
import {
    isClientSdkBusinessError,
    logErrorFromClientSdk,
    trackBusinessError,
} from '@sxm-de/dev-kits/web-sdk/utils';

interface GetProductRedeemUrlFnPayload {
    subscriptionId: string;
    productId: string;
}

export type GetProductRedeemUrlFn = (
    payload: GetProductRedeemUrlFnPayload,
) => Promise<ProductRedeemUrl>;

export function initGetProductRedeemUrlFn({
    dependencies,
}: DomainInitPayload): GetProductRedeemUrlFn {
    const { clientSdk, appEventSdk } = dependencies;
    return async ({
        subscriptionId,
        productId,
    }: GetProductRedeemUrlFnPayload) => {
        let entitlement;
        let url;
        try {
            entitlement = await getPartnerEntitlement(
                { subscriptionId, productId },
                dependencies,
            );
        } catch (error: any) {
            if (isClientSdkBusinessError(error)) {
                sendBusinessError(appEventSdk, error);
            } else {
                logErrorFromClientSdk(error, dependencies);
            }
            throw new Error('SYSTEM');
        }

        if (entitlement) {
            try {
                const { signupUrl } =
                    await clientSdk.commerce.partnerEntitlements.activatePartnerEntitlement(
                        entitlement.id,
                    );
                url = signupUrl;
            } catch (error: any) {
                switch (error.code) {
                    case 'commerceTeam.partnerEntitlementsService.ENTITLEMENT_NOT_FOUND':
                    case 'commerceTeam.partnerEntitlementsService.ENTITLEMENT_UNAVAILABLE_FOR_ACTIVATION':
                        sendBusinessError(appEventSdk, error);
                        break;
                    default:
                        logErrorFromClientSdk(error, dependencies);
                }
                throw new Error('SYSTEM');
            }
        }

        if (url) {
            return {
                url,
            };
        } else {
            sendBusinessError(appEventSdk, {
                code: 'REDEEM_URL_NOT_FOUND',
            });
            throw new Error('SYSTEM');
        }
    };
}

function sendBusinessError(appEventSdk: AppEventSdk, error: any) {
    trackBusinessError(appEventSdk, {
        ...error,
        message: 'Redeem link - Redeem link retrieval failure error.',
    });
}
